// What every interface component reads (WS-49 track T3): the runtime, the texts, what is drawn, and the content
// helpers they share. No page access here, so the components' markup can be tested without a browser.
import { extendRegistry } from '../settings/index.js';
import { placeForKey } from '../content.js';
import { layoutOf } from './layout.js';

/**
 * @param api what main.js hands to createUI (with `runtime` and `tours`)  @param i18n the translator (i18n.js)
 * @param legacy true without a preset: the page runs the implicit preset of today's interface
 */
export function createContext(api, i18n, legacy = true) {
  const rt = api.runtime, reg = api.registry || extendRegistry(); // README.md "Hooks": api.registry carries the package's settings.json
  for (const [l, table] of Object.entries(reg.strings || {})) i18n.add(l, table, true);
  const seasons = () => api.resort.ui?.seasons || [];
  const c = {
    api, rt, reg, i18n, legacy, T: i18n.T, tx: i18n.tx,
    layout: reg.layouts.default,
    slots: {},          // slot -> control ids drawn there (layout.js plan)
    drawn: new Set(),   // every control id that is drawn now
    editing: false,     // WS-51 editor mode is on (ui.js): a press on the map selects whatever the preset shows
    ghostsOn: false,    // WS-51 editor mode: hidden controls are drawn as ghosts (ui.js sets it; never on a visitor's page)
    ghosts: new Set(),  // the control ids drawn as ghosts now (page.js)
    season: api.season || rt.get('season.current') || 'winter', // the season on screen
    flash: null,        // unit labels kept in view for a moment after "show all of a type"
    v: (key) => rt.get(key),
    has: (id) => c.drawn.has(id),
    /** A visitor's press on a control that has no function of its own in the map (language, layers, the panel fold,
     *  the aurora dial): the value is set through the runtime, as a visitor (contract sections 3 and 4). */
    put: (patch) => rt.set(patch, { source: 'visitor' }),
    /** Reads the layout and the language of the moment. */
    read() {
      c.layout = layoutOf(reg, rt.get('layout.id'));
      i18n.use(rt.get('lang.current') || rt.get('lang.default'), rt.get('lang.default'), api.langs?.[0]); // api.langs: the map's languages, the package's own first
    },
    // WS-91: on a wayfinding page (a scanned QR code) "Show where I am" is there whatever the preset lists (api.locate.always)
    // (WS-91 part 2) and "Where to?" in its local QA stand-in (api.wayfind.always: ?qaHere with &qaWayfind on a draft host)
    shown: () => ((ids) => [...ids, ...(api.locate?.always?.() && !ids.includes('locate') ? ['locate'] : []), ...(api.wayfind?.always?.() && !ids.includes('wayfind') ? ['wayfind'] : [])])(rt.controls().filter((x) => x.shown).map((x) => x.id)),
    /** `labels.unitNumbers`: false = room types only (no number on the map, in the list, on the card). */
    units: () => rt.get('labels.unitNumbers') !== false,
    /** `labels.unitBadges`: false = no number badge over the units on the map (names, cards and the list keep theirs). */
    badges: () => c.units() && rt.get('labels.unitBadges') !== false,
    title: (p) => (!p.unit ? i18n.tx(p.title) : c.units() ? i18n.T('unit.title', { name: i18n.tx(p.name), number: p.number }) : i18n.tx(p.name)),
    seasonName: (id) => i18n.named(seasons().find((s) => s.id === id)?.label, i18n.T(`set.season.current.${id}`)) || id,
    unitKeys: (type) => [...api.places.keys()].map((k) => [k, placeForKey(k)]).filter(([, p]) => p?.unit === type).sort((a, b) => a[1].number - b[1].number).map(([k]) => k),
  };
  return c;
}
